import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { 
  TrendingUp, 
  Users, 
  FolderOpen, 
  DollarSign,
  ArrowUpRight,
  BarChart3
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Analytics error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-analytics">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Analytics</h1>
          <p className="text-slate-400">Track performance and conversion metrics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-blue-200" />
              <span className="text-blue-200 text-sm flex items-center gap-1">
                <ArrowUpRight className="w-4 h-4" />
                +{stats?.today?.leads || 0} today
              </span>
            </div>
            <p className="text-4xl font-bold text-white mb-1">{stats?.totals?.leads || 0}</p>
            <p className="text-blue-200">Total Leads</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <FolderOpen className="w-8 h-8 text-purple-200" />
            </div>
            <p className="text-4xl font-bold text-white mb-1">{stats?.totals?.users || 0}</p>
            <p className="text-purple-200">Registered Users</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-emerald-200" />
            </div>
            <p className="text-4xl font-bold text-white mb-1">{stats?.totals?.conversion_rate || 0}%</p>
            <p className="text-emerald-200">Conversion Rate</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-amber-200" />
            </div>
            <p className="text-4xl font-bold text-white mb-1">{formatCurrency(stats?.average_debt)}</p>
            <p className="text-amber-200">Average Debt</p>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Lead Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-[#10B981]" />
              <h2 className="text-lg font-semibold text-white">Lead Acquisition (7 Days)</h2>
            </div>
            <div className="h-64 flex items-end justify-between gap-3">
              {stats?.daily_leads?.map((day, i) => {
                const maxCount = Math.max(...(stats?.daily_leads?.map(d => d.count) || [1]), 1);
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <span className="text-white font-bold text-sm">{day.count}</span>
                    <div 
                      className="w-full bg-gradient-to-t from-[#10B981] to-emerald-400 rounded-t-lg transition-all duration-500"
                      style={{ height: `${Math.max(height, 8)}%` }}
                    ></div>
                    <span className="text-xs text-slate-500">
                      {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Lead Status Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <h2 className="text-lg font-semibold text-white mb-6">Lead Status Distribution</h2>
            <div className="space-y-4">
              {Object.entries(stats?.lead_statuses || {}).map(([status, count]) => {
                const total = Object.values(stats?.lead_statuses || {}).reduce((a, b) => a + b, 0);
                const percent = total > 0 ? (count / total) * 100 : 0;
                const colors = {
                  new: "from-blue-500 to-blue-600",
                  contacted: "from-amber-500 to-amber-600",
                  converted: "from-emerald-500 to-emerald-600",
                  rejected: "from-red-500 to-red-600"
                };
                return (
                  <div key={status}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-300 capitalize">{status}</span>
                      <span className="text-white font-medium">{count} ({percent.toFixed(1)}%)</span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${colors[status] || 'from-slate-500 to-slate-600'} rounded-full transition-all duration-500`}
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Case Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
        >
          <h2 className="text-lg font-semibold text-white mb-6">Case Pipeline</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(stats?.case_statuses || {}).map(([status, count]) => {
              const colors = {
                documents_required: "bg-amber-500",
                under_review: "bg-purple-500",
                proposal_drafting: "bg-indigo-500",
                creditor_voting: "bg-cyan-500",
                active: "bg-emerald-500",
                completed: "bg-green-600"
              };
              return (
                <div key={status} className="text-center p-4 bg-slate-700/50 rounded-xl">
                  <div className={`w-12 h-12 ${colors[status] || 'bg-slate-500'} rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <span className="text-white font-bold text-lg">{count}</span>
                  </div>
                  <p className="text-slate-300 text-xs capitalize">{status.replace(/_/g, ' ')}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Weekly Summary */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <h3 className="text-lg font-semibold text-white mb-4">This Week</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-700/50 rounded-xl">
                <p className="text-3xl font-bold text-white">{stats?.this_week?.leads || 0}</p>
                <p className="text-slate-400 text-sm">New Leads</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-xl">
                <p className="text-3xl font-bold text-white">{stats?.this_week?.users || 0}</p>
                <p className="text-slate-400 text-sm">Registrations</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Today</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-700/50 rounded-xl">
                <p className="text-3xl font-bold text-[#10B981]">{stats?.today?.leads || 0}</p>
                <p className="text-slate-400 text-sm">New Leads</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-xl">
                <p className="text-3xl font-bold text-[#10B981]">{stats?.today?.users || 0}</p>
                <p className="text-slate-400 text-sm">Registrations</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalyticsPage;
